import { format, parse } from "date-fns"
import { Button } from "design-system/atoms/Button"
import type { DateRange } from "design-system/molecules/DatePicker"
import { DateRangePicker } from "design-system/molecules/DatePicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus, SearchIcon, Trash2 } from "lucide-react"
import { CompanySelector } from "modules/backoffice/components/CompanySelector"
import { useBackofficeCampaignsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeCampaignsQuery"
import { useBackofficeGoalsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeGoalsQuery"
import { useDeleteBackofficeClustersMutation } from "modules/backoffice/hooks/clusters/useDeleteBackofficeClustersMutation"
import { useGetBackofficeClusterKeysQuery } from "modules/backoffice/hooks/clusters/useGetBackofficeClusterKeysQuery"
import { useGetBackofficeClusterQuery } from "modules/backoffice/hooks/clusters/useGetBackofficeClusterQuery"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route as ClustersRoute } from "routes/_base/_auth/backoffice/clusters"
import { ClusterDomainEnum } from "schema"
import { BackofficeClustersTable } from "./BackofficeClustersTable"
import { ClusterDetailDialog } from "./ClusterDetailDialog"
import { GenerateClusterModal } from "./GenerateClusterModal"

export const BackofficeClusters = () => {
  const { t } = useTranslation()
  const navigate = ClustersRoute.useNavigate()
  const search = ClustersRoute.useSearch()
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [selectedClusterKeys, setSelectedClusterKeys] = useState<string[]>([])

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!search.from || !search.to) return undefined
    const dateFrom = parse(search.from, "yyyy-MM-dd", new Date())
    const dateTo = parse(search.to, "yyyy-MM-dd", new Date())
    return { from: dateFrom, to: dateTo }
  }, [search.from, search.to])

  const { data: clusters, isPending } = useGetBackofficeClusterKeysQuery({
    query: {
      companyId: search.companyId,
      domain: search.domain,
      goalId: search.goalId,
      campaignId: search.campaignId,
      from: search.from,
      to: search.to,
    },
  })

  const isGoalDomain = search.domain === ClusterDomainEnum.Goal

  const { data: goals } = useBackofficeGoalsQuery({
    companyId: search.companyId ?? "",
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
    enabled: !!search.companyId,
  })

  const { data: campaigns } = useBackofficeCampaignsQuery({
    companyId: search.companyId ?? "",
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
    enabled: isGoalDomain && !!search.companyId,
  })

  const goalsOptions = useMemo(() => {
    return [
      { value: "all", label: t("backoffice.clusters.allGoals") },
      ...(goals?.map((goal) => ({
        value: goal.id,
        label: goal.name,
      })) ?? []),
    ]
  }, [goals, t])

  const campaignsOptions = useMemo(() => {
    return [
      { value: "all", label: t("backoffice.clusters.allCampaigns") },
      ...(campaigns?.map((campaign) => ({
        value: campaign.id,
        label: campaign.name ?? "",
      })) ?? []),
    ]
  }, [campaigns, t])

  const domainOptions = useMemo(() => {
    return [
      { value: "all", label: t("backoffice.clusters.allDomains") },
      ...Object.values(ClusterDomainEnum).map((domain) => ({
        value: domain,
        label: t(`dashboard.domains.${domain}`),
      })),
    ]
  }, [t])

  const handleDateRangeChange = (newDateRange: DateRange | undefined) => {
    if (newDateRange?.from && newDateRange?.to) {
      const newSearch = {
        ...search,
        from: format(newDateRange.from, "yyyy-MM-dd"),
        to: format(newDateRange.to, "yyyy-MM-dd"),
      }

      navigate({ search: newSearch })
      return
    }

    navigate({
      search: {
        ...search,
        from: undefined,
        to: undefined,
      },
    })
  }

  const handleCompanyChange = (companyId: string | undefined) => {
    const newSearch = {
      ...search,
      companyId,
      goalId: undefined,
      campaignId: undefined,
    }
    navigate({ search: newSearch })
  }

  const handleDomainChange = (domain: string) => {
    if (domain === "all") {
      const newSearch = {
        ...search,
        domain: undefined,
        goalId: undefined,
        campaignId: undefined,
      }
      navigate({ search: newSearch })
      return
    }

    const newSearch = {
      ...search,
      domain: domain as (typeof ClusterDomainEnum)[keyof typeof ClusterDomainEnum],
      goalId: domain === ClusterDomainEnum.Goal ? search.goalId : undefined,
      campaignId: domain === ClusterDomainEnum.Goal ? search.campaignId : undefined,
    }
    navigate({ search: newSearch })
  }

  const handleGoalChange = (goalId: string) => {
    const newSearch = {
      ...search,
      goalId: goalId === "all" ? undefined : goalId,
    }
    navigate({ search: newSearch })
  }

  const handleCampaignChange = (campaignId: string) => {
    const newSearch = {
      ...search,
      campaignId: campaignId === "all" ? undefined : campaignId,
    }
    navigate({ search: newSearch })
  }

  const { query: searchQuery, setQuery: setSearchQuery } = useSearchQuery({
    search,
    fullPath: ClustersRoute.fullPath,
  })

  const shouldShowGoalFilter = isGoalDomain && search.companyId

  const { data: cluster, isPending: isClusterPending } = useGetBackofficeClusterQuery({
    query: { key: search.key ?? "" },
    enabled: !!search.key,
  })

  const selectedCluster = useMemo(() => {
    if (!clusters || !search.key) return undefined
    return clusters.find((clusterItem) => clusterItem.key === search.key)
  }, [clusters, search.key])

  const clusterParams = useMemo(() => {
    const from =
      selectedCluster?.from ??
      (search.from ? parse(search.from, "yyyy-MM-dd", new Date()) : undefined)
    const to =
      selectedCluster?.to ?? (search.to ? parse(search.to, "yyyy-MM-dd", new Date()) : undefined)

    return {
      companyId: selectedCluster?.companyId ?? search.companyId,
      domain: selectedCluster?.domain ?? search.domain,
      goalId: selectedCluster?.goalId ?? search.goalId,
      campaignId: selectedCluster?.campaignId,
      from: from ? format(from, "yyyy-MM-dd") : undefined,
      to: to ? format(to, "yyyy-MM-dd") : undefined,
      biasFilter: selectedCluster?.biasFilter,
      score: selectedCluster?.score,
      epsilon: selectedCluster?.epsilon,
      samplingFactor: selectedCluster?.samplingFactor,
      selectionMethod: selectedCluster?.selectionMethod,
    }
  }, [search, selectedCluster])

  const deleteClustersMutation = useDeleteBackofficeClustersMutation()

  useEffect(() => {
    if (!clusters) {
      setSelectedClusterKeys([])
      return
    }

    const clusterKeysSet = new Set(clusters.map((cluster) => cluster.key))
    setSelectedClusterKeys((prev) => prev.filter((key) => clusterKeysSet.has(key)))
  }, [clusters])

  function onCloseDialog(open: boolean) {
    if (!open) {
      const restSearch = { ...search }
      delete restSearch.key
      navigate({ to: "/backoffice/clusters", search: restSearch })
    }
  }

  const handleBulkDelete = async () => {
    if (!selectedClusterKeys.length) return
    await deleteClustersMutation.mutateAsync({ keys: selectedClusterKeys })
    setSelectedClusterKeys([])
  }

  return (
    <>
      <div className="flex flex-col gap-4 w-full h-full">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-2 items-end justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <div className="w-full">
                <SearchQuery
                  placeholder={t("backoffice.clusters.searchCompanyName")}
                  setQuery={setSearchQuery}
                  defaultValue={searchQuery}
                  icon={SearchIcon}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-stretch sm:h-10">
              <div className="w-full sm:w-48">
                <CompanySelector value={search.companyId} onChange={handleCompanyChange} />
              </div>
              <div className="w-full sm:w-52">
                <Select value={search.domain ?? "all"} onValueChange={handleDomainChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("backoffice.clusters.selectDomain")} />
                  </SelectTrigger>
                  <SelectContent>
                    {domainOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {shouldShowGoalFilter && (
                <div className="w-full sm:w-48">
                  <Select
                    value={search.goalId ?? "all"}
                    onValueChange={handleGoalChange}
                    disabled={!search.companyId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("backoffice.clusters.selectGoal")} />
                    </SelectTrigger>
                    <SelectContent>
                      {goalsOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {shouldShowGoalFilter && (
                <div className="w-full sm:w-48">
                  <Select
                    value={search.campaignId ?? "all"}
                    onValueChange={handleCampaignChange}
                    disabled={!search.companyId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("backoffice.clusters.selectCampaign")} />
                    </SelectTrigger>
                    <SelectContent>
                      {campaignsOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="w-full sm:w-56">
                <DateRangePicker
                  dateRange={dateRange}
                  setDateRange={handleDateRangeChange}
                  placeholder={t("shared.dateRange.placeholder")}
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto items-center justify-end">
                <Button onClick={() => setIsGenerateModalOpen(true)} className="sm:h-full">
                  <Plus className="size-4" strokeWidth={2} />
                  {t("backoffice.clusters.generate.button")}
                </Button>
                <Button
                  size="icon"
                  variant="destructive"
                  className="sm:h-full w-10"
                  title={t("backoffice.clusters.deleteSelected")}
                  aria-label={t("backoffice.clusters.deleteSelected")}
                  onClick={handleBulkDelete}
                  disabled={
                    !selectedClusterKeys.length || deleteClustersMutation.isPending || isPending
                  }
                >
                  <Trash2 className="size-4" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <BackofficeClustersTable
          clusters={clusters}
          isPending={isPending}
          searchQuery={searchQuery}
          selectedKeys={selectedClusterKeys}
          onSelectionChange={setSelectedClusterKeys}
        />
      </div>
      <ClusterDetailDialog
        open={!!search.key}
        onOpenChange={onCloseDialog}
        cluster={cluster ?? []}
        params={clusterParams}
        isPending={isClusterPending}
      />
      <GenerateClusterModal
        open={isGenerateModalOpen}
        onOpenChange={setIsGenerateModalOpen}
        defaultValues={{
          companyId: search.companyId,
          domain: search.domain,
          goalId: search.goalId,
          campaignId: search.campaignId,
          from: search.from,
          to: search.to,
        }}
      />
    </>
  )
}
